import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  InjectionToken,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling } from '@angular/router'
import { routes } from './app.routes'
import { AccountPipe } from '@shared/pipes/account.pipe'
import { ApiService } from '@shared/services/api.service'
import { CookieService } from 'ngx-cookie-service'
import { NgxGfmoneyMessagesModule } from '@yuri.karkhachev/ngx-gfmoney-messages'
import { NgxGfmoneyLibModule } from '@yuri.karkhachev/ngx-gfmoney-lib'
import { provideNgxMask } from 'ngx-mask'
import { ModalModule } from 'ngx-bootstrap/modal'
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker'
import { CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common'
import { provideTranslateService } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http'
import { ApiInterceptor } from '@shared/services/api.interceptor'
import { environment } from '@environments/environment'
import { CustomerNavigationService } from '@shared/services/customer-navigation.service'
import { StoreModule } from '@ngrx/store'
import { appReducer } from '@store/app.reducer'
import { AuthGuard } from '@shared/guards/auth.guard'
import localeSv from '@angular/common/locales/sv'
registerLocaleData(localeSv, 'sv')
import * as Sentry from '@sentry/angular'
import { provideAnimations } from '@angular/platform-browser/animations'
import { ErrorHandlerService } from '@shared/services/error-handler.service'

export const WindowToken = new InjectionToken<Window>('Window object', { providedIn: 'root', factory: () => window })

declare global {
  // @ts-ignore
  interface Array<T> {
    range(min: T, max: T, step: T): Array<T>
  }
}

// istanbul ignore next
if (!Array.prototype.range) {
  Array.prototype.range = (from, to, step) =>
    step ? [...Array(Math.floor((to - from) / step) + 1)].map((_, i) => from + i * step) : []
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'sv' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'kr' },
    AccountPipe,
    DatePipe,
    ApiService,
    AuthGuard,
    CookieService,
    CurrencyPipe,
    CustomerNavigationService,
    importProvidersFrom(
      BsDatepickerModule.forRoot(),
      ModalModule.forRoot(),
      NgxGfmoneyLibModule.forRoot({ servicesUrl: environment.servicesUrl }),
      NgxGfmoneyMessagesModule.forRoot({
        allowedFileTypes: 'image/jpeg,image/png,.pdf,.txt',
        apiUrl: environment.apiHost,
        brand: environment.brandName,
        dateFormat: 'yyyy-MM-dd HH:mm',
        homePagePath: '/dashboard-visa',
      }),
      StoreModule.forRoot({ app: appReducer }, {}),
    ),
    {provide: ErrorHandler, useClass: ErrorHandlerService},
    provideNgxMask(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'sv',
      lang: 'sv',
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(
      /* istanbul ignore next */ () => {
        inject(Sentry.TraceService)
      },
    ),
    provideAnimations(),
  ],
}
